import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState } from 'react';
import { X, DollarSign } from 'lucide-react';
import Modal from '../ui/Modal';

interface BulkGroupPriceModalProps {
  groupId: string | null;
  upcomingSessionCount: number;
  totalSessionCount: number;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    groupId: string,
    data: { price: number; notes: string; include_past: boolean }
  ) => Promise<void>;
}

export default function BulkGroupPriceModal({
  groupId,
  upcomingSessionCount,
  totalSessionCount,
  isOpen,
  onClose,
  onConfirm,
}: BulkGroupPriceModalProps) {
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [includePast, setIncludePast] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pastCount = totalSessionCount - upcomingSessionCount;
  const affectedCount = includePast ? totalSessionCount : upcomingSessionCount;

  const handleClose = () => {
    setPrice('');
    setNotes('');
    setIncludePast(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);

    if (!price || parseFloat(price) < 0) {
      setError('Price must be a positive number');
      return;
    }
    if (!notes.trim()) {
      setError('Notes are required to explain the price change');
      return;
    }
    if (!groupId) return;

    setIsLoading(true);
    try {
      await onConfirm(groupId, {
        price: parseFloat(price),
        notes: notes.trim(),
        include_past: includePast,
      });
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update group price');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" animate={false}>
      {/* Header */}
      <div className="sticky top-0 bg-orange-50 border-b border-orange-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
        <div>
          <h2 className="text-lg font-bold text-orange-900">Update Group Price</h2>
          <p className="text-xs text-orange-700">
            Will affect {affectedCount} session{affectedCount !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleClose}
          className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4 sm:p-6 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Sessions scope toggle */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Apply to</p>
          <div className="flex flex-col gap-2">
            <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-lg border-2 transition-colors"
              style={{ borderColor: !includePast ? '#f97316' : '#e5e7eb', background: !includePast ? '#fff7ed' : 'white' }}>
              <input
                type="radio"
                name="scope"
                checked={!includePast}
                onChange={() => setIncludePast(false)}
                className="mt-0.5 accent-orange-500"
              />
              <div>
                <span className="text-sm font-semibold text-gray-800">
                  Upcoming sessions only
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  {upcomingSessionCount} non-cancelled session{upcomingSessionCount !== 1 ? 's' : ''} from today onwards
                </span>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-lg border-2 transition-colors"
              style={{ borderColor: includePast ? '#f97316' : '#e5e7eb', background: includePast ? '#fff7ed' : 'white' }}>
              <input
                type="radio"
                name="scope"
                checked={includePast}
                onChange={() => setIncludePast(true)}
                className="mt-0.5 accent-orange-500"
              />
              <div>
                <span className="text-sm font-semibold text-gray-800">
                  All sessions (including past)
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  {totalSessionCount} non-cancelled session{totalSessionCount !== 1 ? 's' : ''} total
                  {pastCount > 0 && ` — includes ${pastCount} past session${pastCount !== 1 ? 's' : ''}`}
                </span>
              </div>
            </label>
          </div>
        </div>

        {/* New Price */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1">
            <DollarSign className="w-4 h-4 text-orange-500" />
            New Price per Session ({CURRENCY_SYMBOL})
          </label>
          {/* onWheel blur prevents accidental scroll-to-change */}
          <input
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            placeholder="e.g. 35000"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
          />
        </div>

        {/* Notes — always required */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Notes <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={`Explain the price change, e.g. 'Corrected from ${CURRENCY_SYMBOL}70k to ${CURRENCY_SYMBOL}35k — slab pricing update'`}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
          />
          <p className="text-xs text-gray-500 mt-1">Required for audit trail.</p>
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-3 flex gap-3 justify-end">
        <button
          onClick={handleClose}
          disabled={isLoading}
          className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading || !price || !notes.trim()}
          className="px-5 py-2.5 bg-orange-600 text-white rounded-xl font-semibold text-sm hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading
            ? 'Updating…'
            : `Update ${affectedCount} Session${affectedCount !== 1 ? 's' : ''}`}
        </button>
      </div>
    </Modal>
  );
}
